import React, { createContext, useEffect, useState } from "react";
import enTranslations from "@shopify/polaris/locales/en.json";
import { AppProvider } from "@shopify/polaris";
import { BrowserRouter as Router, Route, Switch } from "react-router-dom";
import axios from "axios";
import _ from "lodash";

import Dashboard from "./pages/dashboard";
import Templates from "./pages/templates";
import Settings from "./pages/settings";
import { getShopOrigin, getHost } from "./hooks/getUrlParams";
import Nav from "./components/Nav";
import Checkouts from "./pages/checkouts";
import Orders from "./pages/orders";
import MessageHistory from "./pages/message-history";
import SendMessage from "./pages/sendMessage";
import NoMatch from "./pages/404";
import Automation from "./pages/automation";
import Loading from "./components/Loading";
import CreateOrEditAutomation from "./pages/automation/createOrEdit";
import CreateOrEditTemplate from "./pages/templates/createOrEdit";
import Pricing from "./pages/pricing";
import { createApp } from "@shopify/app-bridge";
import { Redirect } from "@shopify/app-bridge/actions";

const shopOrigin = getShopOrigin();
const host = getHost();

const config = {
  shopOrigin: shopOrigin,
  host: host,
  tabIndex: "0",
};

export const ShopContext = createContext(config);

function App() {
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!shopOrigin || shopOrigin === "" || shopOrigin === "null") {
      console.log("No shop details found");
      setError(true);
      setLoading(false);
    } else {
      axios
        .get(`${process.env.REACT_APP_API_URL}/auth?shop=${shopOrigin}`)
        .then(async (response) => {
          const error = _.get(response, "data.error", false);
          if (error) {
            console.log("By error", error, response);
            setError(true);
          } else {
            const redirect = _.get(response, "data.redirect_uri", false);
            const redirectToAuth = _.get(response, "data.redirect", false);
            if (redirect && window.top === window.self) {
              await setRedirecting(true);
              window.location.assign(redirect);
              return false;
            }
            if (redirectToAuth) {
              await setRedirecting(true);
              const app = createApp({
                apiKey: process.env.REACT_APP_SHOPIFY_API_KEY,
                host: config.host,
              });
              Redirect.create(app).dispatch(Redirect.Action.REMOTE, redirect);
              return false;
            }
            try {
              const shopDetails = _.get(response, "data.shopDetails", {
                name: null,
                email: null,
                domain: null,
                plan_name: null,
                has_active_subscription: null,
              });
              if ("function" === typeof window.FreshworksWidget) {
                window.FreshworksWidget("identify", "ticketForm", {
                  name: shopDetails.name ? shopDetails.name : "",
                  email: shopDetails.email ? shopDetails.email : "",
                });
                window.FreshworksWidget("prefill", "ticketForm", {
                  subject: `Shop domain ${
                    shopDetails.domain ? shopDetails.domain : ""
                  }`,
                  group_id: 82000589186,
                  custom_fields: {
                    plan: shopDetails.has_active_subscription
                      ? "Starter"
                      : "Free",
                  },
                });
              }
            } catch (e) {
              console.log("Unable to set the chat details:", e.message);
            }
          }
          setLoading(false);
        })
        .catch((e) => {
          console.log("In catch", e.message);
          setError(true);
          setLoading(false);
        });
    }
  }, []);

  if (redirecting) {
    return <Loading text="Redirecting, Please wait..." />;
  }

  if (loading) {
    return <Loading text="Loading..." />;
  }

  if (error) {
    return (
      <div style={{ textAlign: "center", marginTop: "50px" }}>
        Unable to process your request!
      </div>
    );
  }

  const tabs = [
    {
      id: "dashboard",
      url: "/",
      accessibilityLabel: "Dashboard",
    },
    {
      id: "checkouts",
      url: "/checkouts",
      accessibilityLabel: "Abandoned Checkouts",
    },
    {
      id: "orders",
      url: "/orders",
      accessibilityLabel: "Orders CRM",
    },
    {
      id: "automations",
      url: "/automations",
      accessibilityLabel: "Automation",
    },
    {
      id: "chat-settings",
      url: "/settings",
      accessibilityLabel: "Chat & Settings",
    },
    {
      id: "pricing",
      url: "/pricing",
      accessibilityLabel: "Billing",
    },
  ];
  return (
    <ShopContext.Provider value={config}>
      <AppProvider i18n={enTranslations}>
        <Router>
          <div>
            <Nav links={tabs} />
          </div>
          <Switch>
            <Route exact path="/">
              <Dashboard />
            </Route>
            <Route exact path="/automations">
              <Automation />
            </Route>
            <Route exact path="/automations/:id/:task/:type">
              <CreateOrEditAutomation />
            </Route>
            <Route exact path="/templates">
              <Templates />
            </Route>
            <Route exact path="/:parent/templates/:id/:task/:type">
              <CreateOrEditTemplate />
            </Route>
            <Route
              exact
              path="/checkouts/send-message/:id/:type/templates/:id/:task"
            >
              <CreateOrEditTemplate />
            </Route>
            <Route exact path="/checkouts/send-message/:id/:type">
              <SendMessage />
            </Route>
            <Route exact path="/orders/send-message/:id/:type">
              <SendMessage />
            </Route>
            <Route exact path="/dashboard">
              <Dashboard />
            </Route>
            <Route exact path="/checkouts">
              <Checkouts />
            </Route>
            <Route exact path="/orders">
              <Orders />
            </Route>
            <Route exact path="/message-history">
              <MessageHistory />
            </Route>
            <Route exact path="/settings">
              <Settings />
            </Route>
            <Route exact path="/pricing">
              <Pricing />
            </Route>

            <Route path="*">
              <NoMatch />
            </Route>
          </Switch>
        </Router>
      </AppProvider>
    </ShopContext.Provider>
  );
}

export default App;
